import React, { useState, useEffect, useRef } from 'react';
import styles from './TastyBites.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';
import { useData } from '../contexts/DataContext';

const TastyBites = ({ onNavigate }) => {
  const { hotelData, loading } = useData();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHoursExpanded, setIsHoursExpanded] = useState(false);
  const [restaurantHours, setRestaurantHours] = useState([]);
  const [menuSections, setMenuSections] = useState([]);
  const pageRef = useRef(null);

  // Process data when hotelData changes
  useEffect(() => {
    if (hotelData) {
      setRestaurantHours(hotelData.openingHours?.restaurant || []);
      
      // Transform menu data from hotelData format to display format
      const menuData = hotelData.menuItems || {};
      const sectionOrder = hotelData.menuSectionOrder || Object.keys(menuData);
      const sectionNames = hotelData.menuSectionNames || {};
      
      // Default section names for backward compatibility
      const defaultSectionNames = {
        starters: 'Starters',
        pubClassics: 'Pub Classics', 
        curries: 'Curries',
        fromTheSea: 'From the Sea',
        burgers: 'Burgers',
        desserts: 'Desserts',
        sides: 'Sides',
        drinks: 'Drinks'
      };
      
      const getSectionTitle = (sectionKey) => {
        return sectionNames[sectionKey] || defaultSectionNames[sectionKey] || sectionKey;
      };
      
      // Create ordered menu sections based on sectionOrder
      const orderedMenuSections = [];
      
      sectionOrder.forEach(sectionKey => {
        if (menuData[sectionKey]?.length > 0) {
          const availableItems = menuData[sectionKey].filter(item => item.available);
          if (availableItems.length > 0) {
            orderedMenuSections.push({
              key: sectionKey,
              title: getSectionTitle(sectionKey),
              items: availableItems.map(item => ({
                name: item.name,
                description: item.description,
                price: item.price
              }))
            });
          }
        }
      });
      
      setMenuSections(orderedMenuSections);
    } else {
      // Fallback data when no hotelData available
      setRestaurantHours([
        { day: 'Monday', hours: 'Closed' },
        { day: 'Tuesday', hours: 'Closed' },
        { day: 'Wednesday', hours: '5:30 PM - 8:30 PM' },
        { day: 'Thursday', hours: '5:30 PM - 8:30 PM' },
        { day: 'Friday', hours: '5:30 PM - 8:30 PM' },
        { day: 'Saturday', hours: '5:30 PM - 8:30 PM' },
        { day: 'Sunday', hours: '12:00 PM - 8:30 PM' }
      ]);
      setMenuSections([]); // Empty menu on error
    }
  }, [hotelData]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Restaurant images for carousel
  const restaurantImages = [
    { src: '/website_images/food_burger.jpg', alt: 'Delicious burger dish' },
    { src: '/website_images/food_lasagne.jpg', alt: 'Fresh lasagne' },
    { src: '/website_images/food_scampi.jpg', alt: 'Scampi dish' },
    { src: '/website_images/restaurant1.jpg', alt: 'Restaurant dining area 1' },
    { src: '/website_images/restaurant2.jpg', alt: 'Restaurant dining area 2' },
    { src: '/website_images/restaurant3.jpg', alt: 'Restaurant dining area 3' }
  ];


  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === restaurantImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? restaurantImages.length - 1 : prevIndex - 1
    );
  };

  const toggleHours = () => {
    setIsHoursExpanded(!isHoursExpanded);
  };

  const renderMenuSection = (section, additionalClasses = '') => (
    <div key={section.title} className={`${styles['menu-section']} ${additionalClasses}`}>
      <h3 className={styles['menu-section-title']}>{section.title}</h3>
      <div className={styles['menu-items']}>
        {section.items.map((item, index) => (
          <div key={index} className={styles['menu-item']}>
            <div className={styles['item-header']}>
              <h4 className={styles['item-name']}>{item.name}</h4>
              <span className={styles['item-price']}>{item.price}</span>
            </div>
            <p className={styles['item-description']}>{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={pageRef} className={styles['tasty-bites-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>Tasty Bites</h1>
        
        {/* Restaurant Description */}
        <div className={styles['description-section']}>
          <p>
            Welcome to our restaurant facilities, where traditional Welsh hospitality meets modern comfort. 
            Our dining area creates a warm and relaxed atmosphere perfect for family meals, romantic dinners, 
            or casual drinks with friends.
          </p>
          <p>
            Our kitchen serves a carefully curated menu featuring locally sourced ingredients and classic pub favorites. 
            Whether you're looking for a hearty meal after exploring Harlech Castle or a light bite with your drink, 
            we have something to satisfy every appetite.
          </p>
        </div>
        
        {/* Image Carousel */}
        <div className={styles['carousel-container']} data-testid="image-carousel">
          <button className={styles['carousel-btn']} onClick={prevImage} aria-label="Previous image">
            ‹
          </button>
          <div className={styles.carousel}>
            <img 
              src={restaurantImages[currentImageIndex].src}
              alt={restaurantImages[currentImageIndex].alt}
              className={styles['carousel-image']}
            />
          </div>
          <button className={styles['carousel-btn']} onClick={nextImage} aria-label="Next image">
            ›
          </button>
        </div>


        {/* Restaurant Opening Hours */}
        <div className={styles['opening-hours-section']}>
          <button className={styles['hours-toggle']} onClick={toggleHours}>
            Restaurant Opening Hours <span className={styles['hours-arrow']}>{isHoursExpanded ? '▲' : '▼'}</span>
          </button>
          <div 
            className={`${styles['hours-content']} ${isHoursExpanded ? styles.expanded : ''}`}
            data-testid="hours-content"
          >
            {loading ? (
              <div className={styles['loading-message']}>Loading hours...</div>
            ) : (
              <div className={styles['hours-grid']}>
                {restaurantHours.map((item, index) => (
                  <div key={index} className={styles['hours-day']}>
                    <span className={styles['day-name']}>{item.day}</span>
                    <span className={styles['day-hours']}>{item.hours}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Menu Sections */}
        <div className={styles['menu-container']}>
          <h2 className={styles['menu-title']}>Our Menu</h2>
          {loading ? (
            <div className={styles['loading-message']}>Loading menu...</div>
          ) : menuSections.length === 0 ? (
            <div className={styles['no-menu-message']}>
              <p>Menu information is currently being updated. Please contact us for current offerings.</p>
            </div>
          ) : (
            <>
              <div className={styles['menu-sections']}>
                {menuSections.map((section, index) => {
                  const isLastSectionAndOdd = index === menuSections.length - 1 && menuSections.length % 2 === 1;
                  const spanningClass = isLastSectionAndOdd ? styles['span-two-columns'] : '';
                  
                  return renderMenuSection(section, spanningClass);
                })}
              </div>
              
              <div className={styles['menu-footer']}>
                <p><em>All meals are good sized portions.</em></p>
                <p><em>Please advise a member of staff if you have food allergies or dietary requirements before ordering.</em></p>
              </div>
            </>
          )}
        </div>
      </div>
      <YouMayAlsoLike currentPage="tasty-bites" onNavigate={onNavigate} />
    </div>
  );
};

export default TastyBites;